import ResourceDetails from "@/components/map/ResourceDetails";
import { useDateFormat } from "@/hooks/useDateFormat";
import ResourceSettingsProvider from "@/hooks/useResourceSettings";
import { IResource } from "@gettactic/api";
import {
  InteractiveZone,
  InteractiveZonesInfo
} from "@gettactic/helpers/src/map/types";
import {
  useAuthenticated,
  useIsDesignSystem2,
  useResources
} from "@gettactic/hooks";
import { useMap } from "@webV2/map/MapProvider";
import MapViewHoverDetails from "@webV2/map/MapView/overlays/MapViewHoverDetails";
import React from "react";
import { createPortal } from "react-dom";

export interface MapViewOverlaysProps {
  svgMapRef?: React.RefObject<SVGSVGElement | null | undefined>;
  zonesInfo: InteractiveZonesInfo;
  mapContainerRef: React.RefObject<HTMLDivElement>;
}

export const MapViewOverlays: React.FC<MapViewOverlaysProps> = ({
  svgMapRef,
  zonesInfo,
  mapContainerRef
}) => {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();
  const formatDate = useDateFormat();

  const isResourceManager = !!(
    authenticatedUser?.user?.organization_user?.permissions || []
  ).includes("resource:manage");

  const [hoveringResource, setHoveringResource] =
    React.useState<IResource | null>(null);

  const [highlightUserId, setHighlightUserId] = React.useState<
    string | undefined
  >();

  const {
    floor,
    allResources: resources,
    startDate,
    endDate,
    selectedResource,
    isMapEditing,
    setSelectedResource,
    isEmbedded,
    isMapBuilderMap,
    poppers,
    openResourceModal,
    setOpenResourceModal,
    editGroupId,
    setEditGroupId,
    events,
    setMapChangesCount,
    fullscreenMapDisclosure,
    isReservationMode,
    startReserve
  } = useMap();

  const isV2 = useIsDesignSystem2();

  React.useEffect(() => {
    const zones = zonesInfo.zones.reduce(
      (acc, zone) => {
        acc[zone.id] = zone;
        return acc;
      },
      {} as Record<string, InteractiveZone>
    );

    const onHighlightResource = (
      resource: IResource | null,
      userId?: string
    ) => {
      if (!resource || !zones[resource?.id || ""]) {
        setHoveringResource(null);
        setHighlightUserId(undefined);
        return;
      }

      setHoveringResource({ ...resource, ...zones[resource.id] });
      setHighlightUserId(userId);
    };
    events.register("onHighlightResource", onHighlightResource);

    return () => {
      events.unregister("onHighlightResource", onHighlightResource);
    };
  }, [zonesInfo.zones]);

  const { resources: floorSpecificResourcesWithEdit } = useResources(
    {
      officeId: currentOffice.id,
      types: [
        "desk",
        "workspace",
        "meeting_room",
        "parking_lot",
        "parking_space",
        "area"
      ],
      parentId: floor ? floor.id : "",
      filter: "",
      query: undefined,
      hierarchy: true,
      manage: isResourceManager
    },
    {
      enabled: (isMapEditing || !!selectedResource) && isResourceManager
    }
  );

  if (!authenticatedUser) return null;

  const formattedDate = formatDate(startDate);

  const allowResourceDetails =
    floor &&
    startDate &&
    (svgMapRef?.current || isMapBuilderMap) &&
    currentOffice &&
    currentOffice.id;

  const meetingsDisabled =
    !authenticatedUser?.organization?.meeting_room_bookings_enabled;

  const openReservation = (resource: IResource) => {
    // Use startReserve to properly open the reserve modal
    // This ensures the modal opens correctly even when closing fullscreen mode
    startReserve(resource);
  };

  return (
    <>
      {allowResourceDetails ? (
        <>
          {createPortal(
            <ResourceSettingsProvider>
              <ResourceDetails
                resourcePermission={
                  authenticatedUser.permissions.settings ||
                  (isResourceManager &&
                    !!floorSpecificResourcesWithEdit?.byId[
                      selectedResource?.id ?? ""
                    ])
                }
                openResourceModal={openResourceModal}
                setOpenResourceModal={setOpenResourceModal}
                startDate={startDate}
                endDate={endDate}
                formattedDate={formattedDate}
                openReservation={openReservation}
                res={selectedResource}
                floor={floor}
                zones={isMapBuilderMap ? [] : zonesInfo.zones}
                resources={resources || []}
                editGroupId={editGroupId}
                svg={svgMapRef?.current!}
                officeId={currentOffice.id}
                setEditGroupId={setEditGroupId}
                setMapChanges={setMapChangesCount}
                setPopperElement={poppers.setPopperResDetailsElement}
                styles={poppers.stylesResDetails}
                attributes={poppers.attributesResDetails}
                officeTimeZone={currentOffice.time_zone}
                meetingsDisabled={meetingsDisabled}
                embedded={isEmbedded}
                setSelectedResource={setSelectedResource}
                restrictionsFromSchedulable={!isV2}
                isReservationMode={isReservationMode}
                fullscreenMapDisclosure={fullscreenMapDisclosure}
              />
            </ResourceSettingsProvider>,
            window.document.body
          )}
        </>
      ) : null}
      <MapViewHoverDetails
        openReservation={openReservation}
        setResource={setSelectedResource}
        resource={hoveringResource}
        floor={floor}
        zones={isMapBuilderMap ? [] : zonesInfo.zones}
        formattedDate={formattedDate}
        setPopperElement={poppers.setPopperDetailsHighlightElement}
        highlightUserId={highlightUserId}
        mapContainerRef={mapContainerRef}
        enablePortal
      />
    </>
  );
};
